import * as React from 'react';
import {
  TypeImplementation,
  Expressions,
  withMeta,
  withSystem,
  getSystem,
  matchExpression,
  type Type,
  type ExpressionPathPart,
} from '@packages/systems/wfdl';
import {
  updateExpressionAction,
  ExpressionEditor,
  updateExpressionAtAction,
  type ExpressionEditorActionPayload,
} from '@packages/systems/ExpressionEditor-utils';
import {EUnion, TUnion, EHole} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  updateAnn,
} from '@packages/utilities/contextual-expression';
import {LogicLabel} from '@packages/systems/flows/shared';
import {
  styledDiv,
  styled,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {type CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {
  Select,
  Option,
  Meta,
} from '@packages/systems/design-system/WebflowSelect';

import {getFieldBySlug, isSupportedField, BASIC_FIELDS} from '../utils';

// const ENHANCED_DROPDOWN_WIDTH = 220;
const atUnionTag = (tag: string): ExpressionPathPart => ({in: 'Union', tag});

const StyledOption = styled(Option)(({theme: {gutters}}) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: gutters.x4,
}));
const LeftAlignHeader = styledDiv(({theme: {gutters}}) => ({
  marginLeft: `-${gutters.x16}px`,
}));
const getFieldHeader = (label: string) => (
  <StyledOption header disabled>
    <LeftAlignHeader>{label}</LeftAlignHeader>
  </StyledOption>
);
const basicHeader = [getFieldHeader('Basic info')];
const getCustomHeader = (hasCustomFields: boolean) =>
  hasCustomFields ? [getFieldHeader('Custom fields')] : [];

const getBasicFieldLabels = ({
  selectedCollection,
  selectedField,
}: {
  selectedCollection: CollectionRecordType;
  selectedField: string;
}) => {
  return selectedCollection
    ? // We'll created a label for each basic field
      BASIC_FIELDS.map((basicField) =>
        getLabelForSelect({
          fieldSlug: basicField,
          collection: selectedCollection,
          selectedField,
        })
      )
    : [];
};

const getCustomSlugs = (slugs: Array<string>) =>
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"name" | "slug"'.
  slugs.filter((slug) => !BASIC_FIELDS.includes(slug));

const getCustomFieldLabels = ({
  selectedCollection,
  allFieldSlugs,
  selectedField,
}: {
  selectedCollection: CollectionRecordType;
  allFieldSlugs: Array<string>;
  selectedField: string;
}) => {
  const customSlugs = getCustomSlugs(allFieldSlugs);
  return selectedCollection
    ? customSlugs.map((fieldSlug) =>
        getLabelForSelect({
          fieldSlug,
          collection: selectedCollection,
          selectedField,
        })
      )
    : [];
};

// Get labels for the select menu
const getLabelForSelect = ({
  fieldSlug,
  collection,
  selectedField,
}: {
  fieldSlug: string;
  collection: CollectionRecordType;
  selectedField: string;
}): React.ReactElement => {
  const field = getFieldBySlug({fieldSlug, collection});
  const isSelected = fieldSlug === selectedField;
  return (
    <StyledOption
      id={`search-by-field-select-${fieldSlug}`}
      key={fieldSlug}
      value={fieldSlug}
      label={field.name}
      selected={isSelected}
    />
  );
};

/** Function that gets the value from an expression
 * and checks that value for emptiness
 * i.e. having no value
 */
const payloadValueIsEmpty = matchExpression({
  Text: (value) => value === '',
  Literal: ({value}) => value === '',
  Union: ({tag, value}) => {
    if (tag !== 'None') {
      return false;
    }

    const rec = Expressions.getRecord(value);
    return rec ? Object.keys(rec).length === 0 : false;
  },
  _: (_) => false,
});

/* const dispatchToSelectedField = ({tag}: {tag: string}) => {
  return dispatch => forwardDispatch(dispatch)(atUnionTag(tag));
};
 */
/** Does the following for the provided expression
 * 1. Updates the type to have a placeholder using the type's own label, or the fallback label
 * 2. Updates the type to have a hidden label variant since we don't want it to display
 */
const updateType = (
  expr: ContextualExpression,
  // @ts-expect-error - TS7006 - Parameter 'fallbackLabel' implicitly has an 'any' type.
  fallbackLabel
) => {
  return updateAnn((annotation) => {
    const annotationType = annotation.type;
    if (!annotationType) {
      return annotation;
    }

    const label = getSystem(annotationType)?.label || fallbackLabel;
    const updatedMeta = withMeta(
      {placeholder: `Enter ${label.toLowerCase()}...`},
      annotationType
    );

    return {
      type: withSystem(
        {
          labelVariant: 'hidden',
        },
        updatedMeta
      ),

      env: annotation.env,

      errors: annotation.errors,
    };
  }, expr);
};

const generateAllFieldSlugs = (
  selectedCollection: CollectionRecordType
): Array<string> => {
  return selectedCollection
    ? selectedCollection.fields.reduce<Array<any>>((acc, field) => {
        // For now, we won't allow querying by non-configurable fields
        // nor by Dynamo fields that are incompatible with Logic
        if (isSupportedField(field)) {
          acc.push(field.slug);
        }
        return acc;
      }, [])
    : [];
};

const getExprPayload = (
  args: ExpressionEditorActionPayload,
  tag: string
): ExpressionEditorActionPayload => {
  if (args.type === 'EXPRESSION_ACTION') {
    const valueIsEmpty = payloadValueIsEmpty(args.payload.newExpression);

    // If the provided payload value is empty
    // We want to update the expression value to be an EHole
    // As we require non-empty vlaues
    const newExpression = valueIsEmpty ? EHole() : args.payload.newExpression;

    return updateExpressionAtAction(newExpression, [
      atUnionTag(tag),
      ...args.key,
    ]);
  }

  if (args.type === 'EXPRESSION_BIND_CHANGED') {
    return updateExpressionAtAction(args.binding || EHole(), [
      atUnionTag(tag),
      ...args.key,
    ]);
  }

  return args;
};

/**
 * Type for choosing what to field and value by which to search for an item
 */
export const createSearchByType = (
  lookupFields: {
    [key: string]: Type;
  },
  selectedCollection: CollectionRecordType
) => {
  const allFieldSlugs = generateAllFieldSlugs(selectedCollection);

  return TypeImplementation.Alias({
    system: {bindUIVariant: 'custom'},
    type: TUnion(lookupFields),
    render: function SearchBy({expr, dispatch}) {
      const searchFieldVal = expr;

      const searchFieldUnion = searchFieldVal
        ? ContextualExpressions.getUnion(stripAnn(searchFieldVal)) || null
        : null;

      const searchField = searchFieldUnion ? searchFieldUnion.tag : '';
      const searchFieldName = searchField
        ? getFieldBySlug({
            fieldSlug: searchField,
            collection: selectedCollection,
          }).name
        : '';

      const dropdownList = React.useMemo(() => {
        const collectionHasCustomFields =
          getCustomSlugs(allFieldSlugs).length > 0;

        // Generate the labels for the dropdown list
        const labels = basicHeader.concat(
          getBasicFieldLabels({
            selectedCollection,
            selectedField: searchField,
          }),
          getCustomHeader(collectionHasCustomFields),
          getCustomFieldLabels({
            allFieldSlugs,
            selectedCollection,
            selectedField: searchField,
          })
        );

        return labels;
      }, [searchField]);

      const handleFieldSelect = (selectedFieldSlug: string, meta: Meta) => {
        if (meta.changeType === 'changeEnd' && selectedFieldSlug) {
          const union = EUnion({
            tag: selectedFieldSlug,
            // We make the value for the union an EHole
            // as we should require this input to be populated
            // for the block to be fully configured
            value: EHole(),
          });
          dispatch(updateExpressionAction(union));
        }
      };

      return (
        <div>
          <LogicLabel label="Search by this field" required />
          <GridWrapper>
            <Select
              id="search-by-field-select"
              data-automation-id="search-by-field-select"
              value={searchField}
              placeholder="Select a field"
              onChange={handleFieldSelect}
              aria-label={`Selected item: ${searchFieldName}`}
            >
              {dropdownList}
            </Select>
            {searchFieldUnion ? (
              <ExpressionEditor
                expression={updateType(searchFieldUnion.value, searchFieldName)}
                dispatch={(exprPayload) =>
                  dispatch(getExprPayload(exprPayload, searchFieldUnion.tag))
                }
              />
            ) : null}
          </GridWrapper>
        </div>
      );
    },
  });
};

const GridWrapper = emotionStyled('div')({
  display: 'grid',
  gridRowGap: tokens.space[2],
});
